import { BenefitTypeOptions } from '@strata/constants';
import { Button } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { WarningDialog } from '@strata/react-dialog';
import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { Grid } from '@strata/react-grid';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { dialogStrings, validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import {
    ProductExpansionColumns,
    productExpansionUploadWarning,
} from 'pages/goLives/ProductExpansionSupport';
import {
    ProductPayerConfigBulkUploadErrorMessage,
    ProductPayerConfigBulkUploadErrorsDialog,
} from 'pages/productConfig/ProductPayerConfigBulkUploadErrors';
import { uploadProductPayerConfigFile } from 'pages/productConfig/ProductPayerConfigBulkUploadSupport';
import { ProductPayerConfigFileUpload } from 'pages/productConfig/ProductPayerConfigFileUpload';
import { handleError } from 'services/ErrorManagement';
import { put, useGet, useGetWithRefetch } from 'services/Http';

const TOAST_TIMEOUT_MS = 5000;

export const ProductExpansionDetails = () => {
    const { segmentId, productExpansionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef(null);

    const goHome = () => navigate(`/go-lives/${segmentId}`);

    const segment = useGet(`/reference/payer_segments/${segmentId}`);
    const { result: productExpansion, refetch: refetchProductExpansion } = useGetWithRefetch(
        `/reference/product_expansions/${productExpansionId}`,
    );

    const [showWarning, setShowWarning] = useState(false);
    const [showUploadWarning, setShowUploadWarning] = useState(false);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);

    const updateProductExpansion = async (values, { setFieldValue, resetForm }) => {
        try {
            await put(`/reference/product_expansions/${segmentId}/${productExpansionId}`, {
                product_expansion_name: values.productExpansionName,
                product_expansion_date: values.productExpansionDate,
                outreach_date: values.outreachDate,
                go_live_date: values.goLiveDate,
                benefit: values.benefit,
            });

            if (values.fileToUpload) {
                try {
                    const validationErrors = await uploadProductPayerConfigFile(
                        [segmentId],
                        values.fileToUpload,
                        { product_expansion_id: productExpansionId },
                    );
                    if (validationErrors) {
                        handleError('File not uploaded. Invalid values found in file.');
                        await setFieldValue('validationErrors', validationErrors);
                        SuccessToast({
                            message: 'Product Expansion Updated Successfully!',
                            timeout: TOAST_TIMEOUT_MS,
                        });
                        return;
                    }
                } catch (uploadErr) {
                    handleError('Product expansion updated but file upload failed.', uploadErr);
                    SuccessToast({
                        message: 'Product Expansion Updated Successfully!',
                        timeout: TOAST_TIMEOUT_MS,
                    });
                    return;
                }
            }

            SuccessToast({
                message: 'Product Expansion Updated Successfully!',
                timeout: TOAST_TIMEOUT_MS,
            });
            resetForm({
                values: {
                    ...values,
                    productSegmentConfigFile: undefined,
                    fileToUpload: undefined,
                    validationErrors: undefined,
                },
            });
            await refetchProductExpansion();
        } catch (err) {
            handleError('Error saving product expansion.', err);
        }
    };

    return (
        <Formik
            initialValues={{
                segmentName: segment?.segment_name,
                productExpansionName: productExpansion?.product_expansion_name || undefined,
                productExpansionDate: productExpansion?.product_expansion_date || undefined,
                benefit: productExpansion?.benefit,
                outreachDate: productExpansion?.outreach_date || undefined,
                goLiveDate: productExpansion?.go_live_date || undefined,
                productSegmentConfigFile: undefined,
                fileToUpload: undefined,
                validationErrors: location.state?.validationErrors,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                productExpansionName: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                productExpansionDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                outreachDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                goLiveDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                productSegmentConfigFile: FileUploadFormik.validator(),
                fileToUpload: Yup.mixed(),
                validationErrors: Yup.array().of(
                    Yup.object({
                        sheet_name: Yup.string().required(),
                        column_name: Yup.string().required(),
                        row_index: Yup.number().required(),
                        msg: Yup.string().required(),
                    }),
                ),
            })}
            onSubmit={updateProductExpansion}
        >
            {(formik) => {
                const canSubmit = formik.isValid && formik.dirty;

                return (
                    <>
                        <WarningDialog
                            dialogTitle={dialogStrings.unsavedChanges.title}
                            isOpen={showWarning}
                            onSecondaryButtonClick={() => setShowWarning(false)}
                            onPrimaryButtonClick={goHome}
                            dialogContent={<p>{dialogStrings.unsavedChanges.body}</p>}
                        />
                        <WarningDialog
                            dialogTitle={productExpansionUploadWarning.title}
                            isOpen={showUploadWarning}
                            primaryButtonLabel="Proceed"
                            secondaryButtonLabel="Cancel"
                            onSecondaryButtonClick={() => setShowUploadWarning(false)}
                            onPrimaryButtonClick={() => {
                                setShowUploadWarning(false);
                                fileInputRef.current?.click();
                            }}
                            dialogContent={<p>{productExpansionUploadWarning.body}</p>}
                        />
                        <ProductPayerConfigBulkUploadErrorsDialog
                            validationErrors={formik.values.validationErrors}
                            showDialog={showValidationErrorDialog}
                            onCloseDialog={() => setShowValidationErrorDialog(false)}
                        />
                        <FormContainer
                            formTitle="Edit Product Expansion"
                            isLoading={!productExpansion}
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={formik.isSubmitting || !canSubmit}
                            onSecondaryButtonClick={() => {
                                if (formik.dirty) {
                                    setShowWarning(true);
                                } else {
                                    goHome();
                                }
                            }}
                            formLeftContent={
                                <>
                                    <TextInputFormik
                                        label="Payer Segment"
                                        id="segmentName"
                                        required
                                        disabled
                                    />
                                    <TextInputFormik
                                        label="Product Expansion Name"
                                        id="productExpansionName"
                                        required
                                    />
                                    <SuggestSingleSelectFormik
                                        id="benefit"
                                        label="Benefit"
                                        required
                                        disabled
                                        values={BenefitTypeOptions}
                                        selectedItem={BenefitTypeOptions.find(
                                            ({ value }) => value === formik.values.benefit,
                                        )}
                                    />
                                    <DateInputFormik
                                        label="Platform Start Date"
                                        id="productExpansionDate"
                                        required
                                    />
                                    <DateInputFormik
                                        label="Outreach Date"
                                        id="outreachDate"
                                        required
                                    />
                                    <DateInputFormik
                                        label="Go-Live Date"
                                        id="goLiveDate"
                                        required
                                    />
                                    <div
                                        className="margin__bottom--small"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <div className="font-semi-bold">Product Details</div>
                                        <Button
                                            label="Upload New File"
                                            onClick={() => setShowUploadWarning(true)}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            minHeight: 280,
                                            maxHeight: 280,
                                            width: '100%',
                                            overflow: 'auto',
                                        }}
                                    >
                                        <Grid
                                            columns={ProductExpansionColumns}
                                            data={
                                                productExpansion?.product_service_oids?.map(
                                                    (oid) => ({ oid }),
                                                ) ?? []
                                            }
                                            sortProperties={[{ id: 'oid', desc: false }]}
                                        />
                                    </div>
                                    <div style={{ maxWidth: '500px' }}>
                                        <ProductPayerConfigFileUpload
                                            inputRef={fileInputRef}
                                            triggerOnly
                                            onFileChange={() =>
                                                formik.setFieldValue('validationErrors', undefined)
                                            }
                                        />
                                        <ProductPayerConfigBulkUploadErrorMessage
                                            validationErrors={formik.values.validationErrors}
                                            onOpenDialog={() => setShowValidationErrorDialog(true)}
                                        />
                                    </div>
                                </>
                            }
                        />
                    </>
                );
            }}
        </Formik>
    );
};
